import { useCallback, useEffect, useRef, useState } from 'react';
import { useInRouterContext, useLocation, useNavigationType, useSearchParams } from 'react-router-dom';
import { SEARCH_BOX_PARAMS, searchTextFromLink } from '../utils/drilldown';

/**
 * The history entry a page is on, and whether the person WENT there.
 *
 * `arrived` is true for a pushed or popped address (a link, a search pick, Back,
 * Forward) and false for a replaced one. Every address a page writes for itself in
 * this app (a tab, a date, an open list) replaces, so `arrived` is what tells a link
 * landing on an open page from the page doing its own bookkeeping.
 *
 * Null with no router around the page, which is how many page tests mount one.
 */
export interface AddressEntry {
  key: string;
  search: string;
  arrived: boolean;
}

export function useAddressEntry(): AddressEntry | null {
  // A component lives inside a router for its whole life or not at all, so which
  // branch runs never changes between renders of the same component (as usePageLine).
  const inRouter = useInRouterContext();
  /* eslint-disable react-hooks/rules-of-hooks */
  const location = inRouter ? useLocation() : null;
  const navigationType = inRouter ? useNavigationType() : null;
  /* eslint-enable react-hooks/rules-of-hooks */
  return location ? { key: location.key, search: location.search, arrived: navigationType !== 'REPLACE' } : null;
}

/** What a link put in the search box. See `link` on useLinkedSearch. */
export interface SearchLink {
  text: string;
}

export type LinkedSearch = [text: string, setText: (next: string) => void, link: SearchLink | null];

/**
 * A page's search text, which a link can fill.
 *
 * Used in place of useState('') for the text of a page's own search box:
 *
 *   const [search, setSearch] = useLinkedSearch();
 *
 * A link to the page names a person, a store or some text: ?employeeCode=, else
 * ?storeCode=, else ?q= (utils/drilldown owns the names). The box opens holding it, so
 * the list is narrowed exactly as if the person had typed it, the box itself is the
 * sign that the page is narrowed, and emptying it is the way out.
 *
 *  • The box is filled AGAIN when another link lands on a page that is already open.
 *    Search picks a result in the same tab and the page does not remount, so a box read
 *    once would leave the second pick in the address and the first one in the list.
 *    Only an address the person went to counts (see useAddressEntry): a link to a page
 *    must be pushed, which is what navigate() and <Link> do unless told otherwise.
 *
 *  • The first time the person changes the box, the link's values leave the address
 *    (replaced, every other value kept), so a refresh or a copied address never brings
 *    back a narrowing the person removed. After that, typing does not touch the address:
 *    the router applies an address a moment late, and a box that waited for it would
 *    lose characters (MyApprovalsPage's date boxes document the same thing).
 *
 *  • A box still holding a link's text untouched is emptied when the person goes on to
 *    an address of the same page that names nothing (Back, the page's own menu row), so
 *    Back undoes a pick. Text the person typed is theirs and stays.
 *
 * `link` is what the box was last filled with from the address: null when it was not,
 * and null again once the person has changed the box. It is a NEW object for every link
 * that lands, even one naming the same thing, so a page that must do more than fill the
 * box (open on every quarter, move a role pick) does it in an effect that depends on
 * `link`, and it runs for a second pick too.
 *
 * On a page that uses this hook the three values belong to it: whenever the box is not
 * holding a link, they are taken out of the address.
 *
 * With no router around the page it is plain state and `link` is always null.
 */
export function useLinkedSearch(): LinkedSearch {
  const inRouter = useInRouterContext();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return inRouter ? useSearchFilledByLinks() : useSearchNoLinkCanFill();
}

function useSearchNoLinkCanFill(): LinkedSearch {
  const [text, setText] = useState('');
  return [text, setText, null];
}

function useSearchFilledByLinks(): LinkedSearch {
  const location = useLocation();
  const arrived = useNavigationType() !== 'REPLACE';
  const [, setSearchParams] = useSearchParams();
  const asked = searchTextFromLink(location.search);

  const [text, setText] = useState(asked);
  const [link, setLink] = useState<SearchLink | null>(() => (asked ? { text: asked } : null));
  const [seenKey, setSeenKey] = useState(location.key);

  // Set while rendering, not in an effect, so the list is never drawn once more for
  // the previous link before the new one takes over.
  if (seenKey !== location.key) {
    setSeenKey(location.key);
    if (arrived) {
      if (asked) {
        setText(asked);
        setLink({ text: asked });
      } else if (link) {
        setText('');
        setLink(null);
      }
    }
  }

  // Read through a ref so the setter below keeps one identity, as useState's does:
  // pages list it in effect dependencies, and one that changed with every address
  // would run those effects again.
  const address = useRef({ key: location.key, asked, setSearchParams });
  address.current = { key: location.key, asked, setSearchParams };
  // The entry whose link values have already been asked out. That request reaches this
  // component a moment late, and until it does every keystroke would ask again.
  const clearedAt = useRef<string | null>(null);

  const takeLinkOut = useCallback(() => {
    const now = address.current;
    if (!now.asked || clearedAt.current === now.key) return;
    clearedAt.current = now.key;
    now.setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const key of SEARCH_BOX_PARAMS) next.delete(key);
      return next;
    }, { replace: true });
  }, []);

  // Asked at once, in the same handler, so it goes BEFORE anything else that handler
  // writes to the address. The router gives every writer the address as last drawn, not
  // as last written, so a later write in the same handler puts the link's values back;
  // the effect below then takes them out again and both changes stand. The other way
  // round (the page's write first, then the box) the page's write would be lost, so a
  // handler that does both changes the box first.
  const change = useCallback((next: string) => {
    setText(next);
    setLink(null);
    takeLinkOut();
  }, [takeLinkOut]);

  useEffect(() => {
    if (!link) takeLinkOut();
  }, [link, location.key, takeLinkOut]);

  return [text, change, link];
}
